import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Field, SelectField } from "./common";
import { EntityDialog } from "./EntityDialog";
import { ModeBlock, modeOf } from "./ModeFields";
import { useList } from "@/lib/hooks";
import {
  CLIENT_TYPES, INTERVENTION_TYPES, INTERVENTION_STATUS, ORDER_CATEGORIES, ORDER_STATUS, REMINDER_KINDS, PRIORITIES, todayStr,
} from "@/lib/constants";

const useClientOptions = (open) => {
  const { items } = useList(open ? "/clients" : "/", undefined);
  return Array.isArray(items) ? items.map((c) => [c.id, c.company ? `${c.name} — ${c.company}` : c.name]) : [];
};

const txt = (form, set, k, testid, props = {}) => (
  <Input value={form[k] ?? ""} onChange={set(k)} data-testid={testid} {...props} />
);

export const ClientDialog = (p) => (
  <EntityDialog
    {...p}
    resource="clients"
    title="client"
    testid="client-dialog"
    defaults={{ type: "particulier", name: "", company: "", email: "", phone: "", address: "", postal_code: "", city: "Saint-Marcellin-en-Forez", notes: "" }}
    render={(f, set) => (
      <>
        <Field label="Type *">
          <SelectField value={f.type} onChange={set("type")} options={CLIENT_TYPES} testid="client-type-select" />
        </Field>
        <Field label="Nom *">{txt(f, set, "name", "client-name-input", { required: true, placeholder: "Nom et prénom" })}</Field>
        {f.type === "professionnel" && (
          <Field label="Société" className="sm:col-span-2">{txt(f, set, "company", "client-company-input", { placeholder: "Raison sociale" })}</Field>
        )}
        <Field label="Téléphone">{txt(f, set, "phone", "client-phone-input", { placeholder: "06 00 00 00 00" })}</Field>
        <Field label="Email">{txt(f, set, "email", "client-email-input", { type: "email", placeholder: "client@exemple.fr" })}</Field>
        <Field label="Adresse" className="sm:col-span-2">{txt(f, set, "address", "client-address-input")}</Field>
        <Field label="Code postal">{txt(f, set, "postal_code", "client-postal-input", { placeholder: "42680" })}</Field>
        <Field label="Ville">{txt(f, set, "city", "client-city-input")}</Field>
        <Field label="Tarif horaire spécifique (facultatif, €/h)" className="sm:col-span-2">
          <Input type="number" step="0.01" min={0} value={f.hourly_rate ?? ""} placeholder="Vide = tarif général de sa catégorie"
            onChange={(e) => set("hourly_rate")(e.target.value === "" ? null : Number(e.target.value))} data-testid="client-rate-input" />
        </Field>
        <Field label="Notes" className="sm:col-span-2">
          <Textarea value={f.notes} onChange={set("notes")} data-testid="client-notes-input" placeholder="Matériel, accès, préférences…" />
        </Field>
      </>
    )}
  />
);

const InterventionFields = ({ f, set, open }) => {
  const clients = useClientOptions(open);
  const { items: users } = useList("/users");
  return (
    <>
      <Field label="Client *" className="sm:col-span-2">
        <SelectField value={f.client_id} onChange={set("client_id")} options={clients} testid="intervention-client-select" placeholder="Choisir un client" />
      </Field>
      {(!f.id || f.draft) && (
        <label className="flex items-start gap-3 rounded-lg border border-amber-400/20 bg-amber-400/5 p-3 text-sm text-slate-200 sm:col-span-2">
          <Switch checked={!!f.draft} onCheckedChange={set("draft")} data-testid="intervention-draft-switch" />
          <span>Enregistrer en brouillon<span className="block text-xs text-slate-400">Invisible du planning et d'Outlook, aucun e-mail. À valider ensuite depuis la fiche.</span></span>
        </label>
      )}
      <Field label="Objet *" className="sm:col-span-2">{txt(f, set, "title", "intervention-title-input", { required: !f.draft, placeholder: "Ex. PC lent, installation box…" })}</Field>
      <ModeBlock f={f} set={set} testid="intervention-mode" />
      <Field label="Type *">
        <SelectField value={f.type} onChange={set("type")} options={INTERVENTION_TYPES} testid="intervention-type-select" />
      </Field>
      <Field label="Statut *">
        {f.mode === "atelier"
          ? <p className="flex h-10 items-center rounded-md border border-white/10 px-3 text-sm text-slate-300" data-testid="intervention-status-select">{INTERVENTION_STATUS[f.status] || "—"} · suit l'étape atelier</p>
          : <SelectField value={f.status} onChange={set("status")} options={INTERVENTION_STATUS} testid="intervention-status-select" />}
      </Field>
      <Field label="Date et heure">{txt(f, set, "scheduled_at", "intervention-date-input", { type: "datetime-local" })}</Field>
      <Field label="Durée (minutes)">
        <Input type="number" min={15} step={15} value={f.duration_minutes} onChange={(e) => set("duration_minutes")(Number(e.target.value))} data-testid="intervention-duration-input" />
        <p className="mt-1 text-xs text-slate-400" data-testid="intervention-duration-hint">Valeur par défaut : 60 min, modifiable. Utilisée pour Outlook et l'e-mail client.</p>
      </Field>
      {modeOf(f) === "site" && <Field label="Lieu d'intervention" className="sm:col-span-2">{txt(f, set, "location", "intervention-location-input", { placeholder: "Vide = adresse du client" })}</Field>}
      <Field label="Résumé destiné au client" className="sm:col-span-2">
        <Textarea value={f.client_summary} onChange={set("client_summary")} data-testid="intervention-client-summary-input" placeholder="Visible dans l'e-mail client et le rendez-vous Outlook. Pas de mot de passe ni de note interne." />
      </Field>
      <label className="flex items-start gap-3 rounded-lg border border-cyan-400/15 bg-white/[0.03] p-3 text-sm text-slate-200 sm:col-span-2">
        <Switch checked={f.notify_client !== false} onCheckedChange={set("notify_client")} data-testid="intervention-notify-switch" />
        <span>Notifier le client par e-mail<span className="block text-xs text-slate-400">Confirmation, modification et annulation du rendez-vous. Ce choix s'applique aussi aux changements faits depuis Outlook.</span></span>
      </label>
      <Field label="Technicien" className="sm:col-span-2">
        <SelectField value={f.technician_id} onChange={set("technician_id")} options={users.map((u) => [u.id, u.name || u.email])} allowNone="Non assigné" testid="intervention-technician-select" />
      </Field>
      <Field label="Priorité"><SelectField value={f.priority || "normale"} onChange={set("priority")} options={PRIORITIES} testid="intervention-priority-select" /></Field>
      <Field label="Notes internes (jamais envoyées au client)" className="sm:col-span-2">
        <Textarea value={f.internal_notes || ""} onChange={set("internal_notes")} data-testid="intervention-internal-notes-input" />
      </Field>
      <Field label="Description (interne)" className="sm:col-span-2">
        <Textarea value={f.description} onChange={set("description")} data-testid="intervention-description-input" />
      </Field>
      <Field label="Compte rendu" className="sm:col-span-2">
        <Textarea value={f.report} onChange={set("report")} data-testid="intervention-report-input" placeholder="Travaux réalisés, pièces changées…" />
      </Field>
    </>
  );
};

const unscheduleConfirm = (f, initial) =>
  initial?.outlook_event_id && initial?.scheduled_at && !f.scheduled_at
    ? "Retirer la date supprimera le rendez-vous lié dans votre calendrier Outlook. La fiche et son historique sont conservés."
    : null;

const notifyToast = (data) => {
  const s = data?.last_notification?.status;
  if (s === "no_email") toast.warning("Intervention enregistrée. Notification non envoyée : le client n'a pas d'adresse e-mail.");
  else if (s === "pending" && data?.notify_client) toast.info("E-mail client en attente d'envoi (sous 2 minutes).");
};

export const InterventionDialog = (p) => (
  <EntityDialog
    {...p}
    resource="interventions"
    title="intervention"
    testid="intervention-dialog"
    confirmBeforeSave={unscheduleConfirm}
    afterSave={notifyToast}
    defaults={{ client_id: "", title: "", type: "depannage", status: "a_planifier", scheduled_at: "", duration_minutes: 60, technician_id: null, description: "", report: "", location: "", client_summary: "", notify_client: true }}
    render={(f, set) => <InterventionFields f={f} set={set} open={p.open} />}
  />
);

const OrderFields = ({ f, set, open }) => {
  const clients = useClientOptions(open);
  return (
    <>
      <Field label="Matériel *" className="sm:col-span-2">{txt(f, set, "item", "order-item-input", { required: true, placeholder: "Ex. PC portable 15\" i5 16 Go" })}</Field>
      <Field label="Catégorie *">
        <SelectField value={f.category} onChange={set("category")} options={ORDER_CATEGORIES} testid="order-category-select" />
      </Field>
      <Field label="État *">
        <SelectField value={f.status} onChange={set("status")} options={ORDER_STATUS} testid="order-status-select" />
      </Field>
      <Field label="Quantité">
        <Input type="number" min={1} value={f.quantity} onChange={(e) => set("quantity")(Number(e.target.value))} data-testid="order-quantity-input" />
      </Field>
      <Field label="Prix d'achat unitaire (€)">
        <Input type="number" step="0.01" value={f.unit_price ?? ""} onChange={(e) => set("unit_price")(e.target.value === "" ? null : Number(e.target.value))} data-testid="order-price-input" />
      </Field>
      <Field label="Prix de vente unitaire (€)">
        <Input type="number" step="0.01" min={0} value={f.sale_price ?? ""} onChange={(e) => set("sale_price")(e.target.value === "" ? null : Number(e.target.value))} data-testid="order-sale-price-input" />
      </Field>
      <Field label="Fournisseur">{txt(f, set, "supplier", "order-supplier-input", { placeholder: "[Nom du fournisseur]" })}</Field>
      <Field label="Référence / n° commande">{txt(f, set, "reference", "order-reference-input")}</Field>
      <Field label="Client concerné">
        <SelectField value={f.client_id} onChange={set("client_id")} options={clients} allowNone="Stock DTech42" testid="order-client-select" />
      </Field>
      <Field label="Livraison prévue">{txt(f, set, "expected_date", "order-date-input", { type: "date" })}</Field>
      <Field label="Notes" className="sm:col-span-2">
        <Textarea value={f.notes} onChange={set("notes")} data-testid="order-notes-input" />
      </Field>
    </>
  );
};

export const OrderDialog = (p) => (
  <EntityDialog
    {...p}
    resource="orders"
    title="commande"
    testid="order-dialog"
    defaults={{ client_id: null, category: "pc", item: "", quantity: 1, supplier: "", reference: "", unit_price: null, sale_price: null, status: "a_commander", expected_date: null, notes: "" }}
    render={(f, set) => <OrderFields f={f} set={set} open={p.open} />}
  />
);

const ReminderFields = ({ f, set, open }) => {
  const clients = useClientOptions(open);
  return (
    <>
      <Field label="Intitulé *" className="sm:col-span-2">{txt(f, set, "title", "reminder-title-input", { required: true, placeholder: "Ex. Appeler pour vérifier le PC" })}</Field>
      <Field label="Type *">
        <SelectField value={f.kind} onChange={set("kind")} options={REMINDER_KINDS} testid="reminder-kind-select" />
      </Field>
      <Field label="Échéance *">{txt(f, set, "due_date", "reminder-date-input", { type: "date", required: true })}</Field>
      <Field label="Client">
        <SelectField value={f.client_id} onChange={set("client_id")} options={clients} allowNone="Aucun" testid="reminder-client-select" />
      </Field>
      <Field label="Récurrence">
        <SelectField
          value={String(f.recurrence_months)}
          onChange={(v) => set("recurrence_months")(Number(v))}
          options={[["0", "Aucune"], ["1", "Tous les mois"], ["3", "Tous les 3 mois"], ["6", "Tous les 6 mois"], ["12", "Tous les ans"]]}
          testid="reminder-recurrence-select"
        />
      </Field>
      {f.kind === "maintenance" && (
        <div className="flex flex-col gap-3 rounded-lg border border-cyan-400/15 bg-white/[0.03] p-3 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
          <label className="flex items-center gap-3 text-sm text-slate-200">
            <Switch checked={!!f.notify_client} onCheckedChange={set("notify_client")} data-testid="reminder-notify-switch" />
            Prévenir le client par email automatiquement
          </label>
          {f.notify_client && (
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <Input type="number" min={0} max={60} className="h-8 w-20" value={f.notify_days_before} onChange={(e) => set("notify_days_before")(Number(e.target.value))} data-testid="reminder-notify-days-input" />
              jours avant
            </div>
          )}
        </div>
      )}
      <Field label="Notes" className="sm:col-span-2">
        <Textarea value={f.notes} onChange={set("notes")} data-testid="reminder-notes-input" />
      </Field>
    </>
  );
};

export const ReminderDialog = (p) => (
  <EntityDialog
    {...p}
    resource="reminders"
    title="rappel"
    testid="reminder-dialog"
    defaults={{ client_id: null, intervention_id: null, kind: "suivi", title: "", due_date: todayStr(), recurrence_months: 0, done: false, notes: "", notify_client: true, notify_days_before: 3 }}
    render={(f, set) => <ReminderFields f={f} set={set} open={p.open} />}
  />
);
